import React, { useState, useEffect } from 'react';
import TouchButton from '../ui/TouchButton';
import { TouchInput, TouchFormGroup } from '../ui/TouchForm';

const FilterPresets = ({ 
  onApplyPreset, 
  currentFilters, 
  onClose 
}) => {
  const [savedPresets, setSavedPresets] = useState([]);
  const [presetName, setPresetName] = useState('');
  const [showSaveForm, setShowSaveForm] = useState(false);

  // Built-in presets
  const builtInPresets = [
    {
      id: 'safe-tourist-spots',
      name: '🛡️ Safe Tourist Spots',
      description: 'Highly rated, verified tourist attractions',
      filters: {
        safetyZones: ['safe'],
        minSafetyRating: 4.0,
        locationTypes: ['attraction', 'museum', 'park'],
        minUserRating: 4,
        minReviewCount: 10,
        hasPhotos: true,
        verifiedOnly: true
      }
    },
    {
      id: 'dining-nightlife',
      name: '🍽️ Dining & Nightlife',
      description: 'Popular restaurants and nightlife venues',
      filters: {
        safetyZones: ['safe', 'moderate'],
        locationTypes: ['restaurant', 'nightlife'],
        categories: ['dining', 'entertainment'],
        minUserRating: 3,
        minReviewCount: 5,
        hasPhotos: true
      }
    },
    {
      id: 'emergency-services',
      name: '🚨 Emergency Services',
      description: 'Hospitals, police stations, and emergency facilities',
      filters: {
        locationTypes: ['hospital', 'police', 'pharmacy'],
        categories: ['health', 'emergency'],
        verifiedOnly: true,
        reviewTimeRange: 'last_year'
      }
    },
    {
      id: 'budget-friendly',
      name: '💰 Budget-Friendly',
      description: 'Affordable options with good ratings',
      filters: {
        priceRange: ['$', '$$'],
        minUserRating: 3,
        minReviewCount: 5,
        hasReviews: true
      }
    },
    {
      id: 'luxury-premium',
      name: '✨ Luxury & Premium',
      description: 'High-end, premium rated locations',
      filters: {
        safetyZones: ['safe'],
        minSafetyRating: 4.5,
        minUserRating: 4,
        minReviewCount: 25,
        priceRange: ['$$$', '$$$$'],
        hasPhotos: true,
        verifiedOnly: true
      }
    },
    {
      id: 'family-friendly',
      name: '👨‍👩‍👧‍👦 Family-Friendly',
      description: 'Safe, accessible places suitable for families',
      filters: {
        safetyZones: ['safe'],
        minSafetyRating: 4.0,
        locationTypes: ['park', 'museum', 'attraction', 'restaurant'],
        accessibility: ['wheelchair', 'accessible-parking'],
        minUserRating: 4,
        hasPhotos: true
      }
    },
    {
      id: 'recent-reviews',
      name: '🆕 Recently Reviewed',
      description: 'Locations with recent activity and reviews',
      filters: {
        reviewTimeRange: 'last_month',
        lastUpdated: 'last_week',
        minReviewCount: 3,
        hasReviews: true
      }
    },
    {
      id: 'transportation-hubs',
      name: '🚇 Transportation',
      description: 'All transportation options and hubs',
      filters: {
        locationTypes: ['transport', 'bus-station', 'train-station', 'subway', 'airport'],
        categories: ['transportation'],
        verifiedOnly: true
      }
    }
  ];

  // Load saved presets from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('filterPresets');
    if (saved) {
      try {
        setSavedPresets(JSON.parse(saved));
      } catch (error) {
        console.error('Failed to load saved presets:', error);
      }
    }
  }, []);

  const saveCurrentFilters = () => {
    if (!presetName.trim()) {
      alert('Please enter a preset name');
      return;
    }

    const newPreset = {
      id: Date.now().toString(),
      name: presetName.trim(),
      description: 'Custom preset',
      filters: currentFilters,
      createdAt: new Date().toISOString(),
      isCustom: true
    };

    const updatedPresets = [...savedPresets, newPreset];
    setSavedPresets(updatedPresets);
    localStorage.setItem('filterPresets', JSON.stringify(updatedPresets));
    
    setPresetName('');
    setShowSaveForm(false);
  };

  const deletePreset = (presetId) => {
    if (window.confirm('Are you sure you want to delete this preset?')) {
      const updatedPresets = savedPresets.filter(preset => preset.id !== presetId);
      setSavedPresets(updatedPresets);
      localStorage.setItem('filterPresets', JSON.stringify(updatedPresets));
    }
  };

  const getActiveFilterCount = (filters) => {
    let count = 0;
    Object.entries(filters).forEach(([key, value]) => {
      if (Array.isArray(value) && value.length > 0) count++;
      else if (value !== null && value !== '' && value !== false && value !== 'all') count++;
    });
    return count;
  };

  const getFilterSummary = (filters) => {
    const summary = [];
    
    if (filters.safetyZones && filters.safetyZones.length > 0) {
      summary.push(`Safety: ${filters.safetyZones.join(', ')}`);
    }
    
    if (filters.locationTypes && filters.locationTypes.length > 0) {
      summary.push(`Types: ${filters.locationTypes.length} selected`);
    }
    
    if (filters.minUserRating) {
      summary.push(`Rating: ${filters.minUserRating}+ stars`);
    }
    
    if (filters.distanceRadius) {
      const km = filters.distanceRadius >= 1000 ? 
        `${(filters.distanceRadius / 1000).toFixed(1)}km` : 
        `${filters.distanceRadius}m`;
      summary.push(`Distance: ${km}`);
    }
    
    return summary.join(' • ');
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h4 style={{ margin: 0, fontSize: '16px', fontWeight: '600' }}>
          📋 Filter Presets
        </h4>
        <div style={{ display: 'flex', gap: '8px' }}>
          <TouchButton
            onClick={() => setShowSaveForm(!showSaveForm)}
            size="small"
            variant="outline"
          >
            💾 Save Current
          </TouchButton>
          <TouchButton
            onClick={onClose}
            size="small"
            variant="ghost"
          >
            ✕
          </TouchButton>
        </div>
      </div>

      {/* Save Current Filters Form */}
      {showSaveForm && (
        <div style={{
          padding: '16px',
          backgroundColor: '#f8f9fa',
          borderRadius: '8px',
          border: '1px solid #e9ecef'
        }}>
          <TouchFormGroup>
            <TouchInput
              placeholder="Enter preset name..."
              value={presetName}
              onChange={(e) => setPresetName(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  saveCurrentFilters();
                }
              }}
            />
            <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
              <TouchButton
                onClick={saveCurrentFilters}
                size="small"
                disabled={!presetName.trim()}
              >
                Save Preset
              </TouchButton>
              <TouchButton
                onClick={() => setShowSaveForm(false)}
                size="small"
                variant="ghost"
              >
                Cancel
              </TouchButton>
            </div>
          </TouchFormGroup>
          
          <div style={{
            fontSize: '12px',
            color: '#666',
            marginTop: '8px',
            padding: '8px',
            backgroundColor: '#e3f2fd',
            borderRadius: '4px'
          }}>
            <strong>Current filters:</strong> {getActiveFilterCount(currentFilters)} active filters
          </div>
        </div>
      )}

      {/* Built-in Presets */}
      <div>
        <h5 style={{ 
          fontSize: '14px', 
          fontWeight: '600', 
          marginBottom: '12px',
          color: '#333'
        }}>
          🎯 Built-in Presets:
        </h5>
        
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', 
          gap: '12px'
        }}>
          {builtInPresets.map(preset => (
            <PresetCard
              key={preset.id}
              preset={preset}
              onApply={() => onApplyPreset(preset)}
            />
          ))}
        </div>
      </div>

      {/* Saved Presets */}
      {savedPresets.length > 0 && (
        <div>
          <h5 style={{ 
            fontSize: '14px', 
            fontWeight: '600', 
            marginBottom: '12px',
            color: '#333'
          }}>
            💾 Your Saved Presets:
          </h5>
          
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', 
            gap: '12px'
          }}>
            {savedPresets.map(preset => (
              <PresetCard
                key={preset.id}
                preset={preset}
                onApply={() => onApplyPreset(preset)}
                onDelete={preset.isCustom ? () => deletePreset(preset.id) : null}
                showDate={true}
              />
            ))}
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div>
        <h5 style={{ 
          fontSize: '14px', 
          fontWeight: '600', 
          marginBottom: '12px',
          color: '#333'
        }}>
          ⚡ Quick Actions:
        </h5>
        
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
          <TouchButton
            onClick={() => onApplyPreset({ filters: {} })} // Clear all filters
            size="small"
            variant="outline"
          >
            🗑️ Clear All Filters
          </TouchButton>
          
          <TouchButton
            onClick={() => onApplyPreset(builtInPresets.find(p => p.id === 'safe-tourist-spots'))}
            size="small"
            variant="outline"
          >
            🛡️ Safe Mode
          </TouchButton>
          
          <TouchButton
            onClick={() => onApplyPreset(builtInPresets.find(p => p.id === 'recent-reviews'))}
            size="small"
            variant="outline"
          >
            🆕 Recent Activity
          </TouchButton>
        </div>
      </div>
    </div>
  );
};

const PresetCard = ({ preset, onApply, onDelete, showDate }) => (
  <div style={{
    padding: '16px',
    backgroundColor: 'white',
    borderRadius: '8px',
    border: '1px solid #e0e0e0',
    transition: 'all 150ms ease'
  }}
  onMouseEnter={(e) => {
    e.target.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
  }}
  onMouseLeave={(e) => {
    e.target.style.boxShadow = 'none';
  }}>
    <div style={{ marginBottom: '12px' }}>
      <div style={{ 
        fontSize: '14px', 
        fontWeight: '600', 
        marginBottom: '4px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start'
      }}>
        <span>{preset.name}</span>
        {preset.isCustom && (
          <span style={{
            backgroundColor: '#e3f2fd',
            color: '#1976D2',
            padding: '2px 6px',
            borderRadius: '10px',
            fontSize: '10px',
            fontWeight: '600'
          }}>
            CUSTOM
          </span>
        )}
      </div>
      
      <div style={{ 
        fontSize: '12px', 
        color: '#666',
        marginBottom: '8px'
      }}>
        {preset.description}
      </div>
      
      {showDate && preset.createdAt && (
        <div style={{ fontSize: '11px', color: '#999' }}>
          Created: {new Date(preset.createdAt).toLocaleDateString()}
        </div>
      )}
    </div>
    
    <div style={{ 
      fontSize: '11px', 
      color: '#666',
      marginBottom: '12px',
      minHeight: '30px'
    }}>
      {getFilterSummary(preset.filters) || 'No active filters'}
    </div>
    
    <div style={{ display: 'flex', gap: '8px' }}>
      <TouchButton
        onClick={onApply}
        size="small"
        style={{ flex: 1 }}
      >
        Apply
      </TouchButton>
      
      {onDelete && (
        <TouchButton
          onClick={onDelete}
          size="small"
          variant="ghost"
          style={{ color: '#F44336' }}
        >
          🗑️
        </TouchButton>
      )}
    </div>
  </div>
);

// Utility function to get filter summary
const getFilterSummary = (filters) => {
  const summary = [];
  
  if (filters.safetyZones && filters.safetyZones.length > 0) {
    summary.push(`Safety: ${filters.safetyZones.length} zones`);
  }
  
  if (filters.locationTypes && filters.locationTypes.length > 0) {
    summary.push(`${filters.locationTypes.length} location types`);
  }
  
  if (filters.minUserRating) {
    summary.push(`${filters.minUserRating}+ stars`);
  }
  
  if (filters.reviewTimeRange && filters.reviewTimeRange !== 'all') {
    summary.push('Recent reviews');
  }
  
  if (filters.verifiedOnly) {
    summary.push('Verified only');
  }
  
  return summary.slice(0, 3).join(' • ');
};

export default FilterPresets;